import { Plus, X } from 'lucide-react'
import {
  CONDITION_OPERATORS,
  type Condition,
  type FormField,
  type Scalar,
  type SimpleOperator,
} from '@concours/shared'
import { Alert, Button, Input, Select } from '@concours/ui'

type Simple = Extract<Condition, { field: string }>
type Mode = 'always' | 'and' | 'or'

/** Ramène une condition à « toujours / toutes / au moins une » de conditions simples. */
function decompose(condition: Condition | undefined): { mode: Mode; rows: Simple[] } | null {
  if (!condition || condition.op === 'always') return { mode: 'always', rows: [] }
  if (condition.op === 'and' || condition.op === 'or') {
    if (condition.conditions.every((c) => 'field' in c))
      return { mode: condition.op, rows: condition.conditions as Simple[] }
    return null
  }
  if ('field' in condition) return { mode: 'and', rows: [condition] }
  return null
}

function compose(mode: Mode, rows: Simple[]): Condition | undefined {
  if (mode === 'always' || !rows.length) return undefined
  return rows.length === 1 ? rows[0] : { op: mode, conditions: rows }
}

const ORDERED = ['integer', 'decimal', 'date', 'computed']

function operatorsFor(field: FormField | undefined): SimpleOperator[] {
  if (!field) return ['eq']
  const base: SimpleOperator[] = ['eq', 'neq', 'filled']
  if (field.options?.length) base.splice(2, 0, 'in')
  if (ORDERED.includes(field.type)) base.push('gt', 'gte', 'lt', 'lte')
  if (field.type === 'checkbox') return ['eq', 'filled']
  return base
}

/**
 * Éditeur de règle d'affichage, SANS code : l'administrateur compose des
 * conditions simples (champ · opérateur · valeur) combinées par « toutes » ou
 * « au moins une ». Seuls les champs situés AVANT sont proposés.
 */
export function ConditionEditor({
  value,
  onChange,
  fields,
  subject,
}: {
  value: Condition | undefined
  onChange: (next: Condition | undefined) => void
  fields: FormField[]
  subject: string
}) {
  const parsed = decompose(value)
  if (!parsed)
    return (
      <Alert tone="info" title="Condition avancée">
        Cette condition combine plusieurs niveaux ; elle reste appliquée telle quelle.{' '}
        <Button variant="ghost" size="sm" onClick={() => onChange(undefined)}>
          Remplacer par « toujours affiché »
        </Button>
      </Alert>
    )
  const { mode, rows } = parsed
  const usable = fields.filter((f) => f.type !== 'readonly' && !['file', 'image'].includes(f.type))
  const update = (nextRows: Simple[], nextMode: Mode = mode === 'always' ? 'and' : mode) =>
    onChange(compose(nextMode, nextRows))

  const newRow = (): Simple | null => {
    const first = usable[0]
    if (!first) return null
    if (first.options?.length) return { op: 'eq', field: first.key, value: first.options[0]!.value }
    if (first.type === 'checkbox') return { op: 'eq', field: first.key, value: true }
    return { op: 'filled', field: first.key }
  }

  return (
    <div className="stack" style={{ gap: 'var(--space-3)' }}>
      <Select
        label={`Affichage ${subject}`}
        value={mode}
        onChange={(e) => {
          const next = e.target.value as Mode
          if (next === 'always') onChange(undefined)
          else if (!rows.length) {
            const row = newRow()
            if (row) update([row], next)
          } else update(rows, next)
        }}
        options={[
          { value: 'always', label: 'Toujours affiché' },
          { value: 'and', label: 'Si TOUTES les conditions sont vraies' },
          { value: 'or', label: 'Si AU MOINS UNE condition est vraie' },
        ]}
        disabled={!usable.length}
        hint={
          usable.length ? undefined : 'Aucun champ situé avant : rien sur quoi poser une condition.'
        }
      />
      {mode !== 'always' &&
        rows.map((row, i) => {
          const field = usable.find((f) => f.key === row.field)
          const ops = operatorsFor(field)
          const setRow = (next: Simple) => update(rows.map((r, j) => (j === i ? next : r)))
          return (
            <div key={i} className="condition-row">
              <Select
                label="Champ"
                value={row.field}
                options={usable.map((f) => ({ value: f.key, label: f.label }))}
                onChange={(e) => {
                  const target = usable.find((f) => f.key === e.target.value)
                  setRow(
                    target?.options?.length
                      ? { op: 'eq', field: e.target.value, value: target.options[0]!.value }
                      : { op: 'filled', field: e.target.value }
                  )
                }}
              />
              <Select
                label="Opérateur"
                value={row.op}
                options={ops.map((op) => ({ value: op, label: CONDITION_OPERATORS[op] }))}
                onChange={(e) => {
                  const op = e.target.value as SimpleOperator
                  if (op === 'filled') setRow({ op, field: row.field })
                  else if (op === 'in')
                    setRow({
                      op,
                      field: row.field,
                      values: field?.options?.slice(0, 1).map((o) => o.value) ?? [],
                    })
                  else if (op === 'eq' || op === 'neq')
                    setRow({
                      op,
                      field: row.field,
                      value:
                        'value' in row ? (row.value as Scalar) : (field?.options?.[0]?.value ?? ''),
                    })
                  else
                    setRow({
                      op,
                      field: row.field,
                      value: 'value' in row && typeof row.value !== 'boolean' ? row.value : 0,
                    })
                }}
              />
              <ValueInput row={row} field={field} onChange={setRow} />
              <Button
                variant="ghost"
                iconOnly
                icon={<X />}
                aria-label={`Retirer la condition ${i + 1}`}
                onClick={() => update(rows.filter((_, j) => j !== i))}
              />
            </div>
          )
        })}
      {mode !== 'always' && (
        <div>
          <Button
            variant="secondary"
            size="sm"
            icon={<Plus />}
            onClick={() => {
              const row = newRow()
              if (row) update([...rows, row])
            }}
          >
            Ajouter une condition
          </Button>
        </div>
      )}
    </div>
  )
}

function ValueInput({
  row,
  field,
  onChange,
}: {
  row: Simple
  field?: FormField
  onChange: (row: Simple) => void
}) {
  if (row.op === 'filled') return <span className="text-muted condition-value">—</span>
  if (row.op === 'in') {
    const selected = new Set(row.values.map(String))
    return (
      <fieldset className="condition-value choice-group">
        <legend className="field-label">Valeurs</legend>
        <div className="choice-options">
          {field?.options?.map((o) => (
            <label key={o.value} className="check-row">
              <input
                type="checkbox"
                checked={selected.has(o.value)}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...selected, o.value]
                    : [...selected].filter((v) => v !== o.value)
                  onChange({ ...row, values: next })
                }}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
    )
  }
  if (field?.type === 'checkbox')
    return (
      <Select
        label="Valeur"
        fieldClassName="condition-value"
        value={String(row.value)}
        options={[
          { value: 'true', label: 'Cochée' },
          { value: 'false', label: 'Non cochée' },
        ]}
        onChange={(e) => onChange({ ...row, value: e.target.value === 'true' } as Simple)}
      />
    )
  if (field?.options?.length && (row.op === 'eq' || row.op === 'neq'))
    return (
      <Select
        label="Valeur"
        fieldClassName="condition-value"
        value={String(row.value)}
        options={field.options}
        onChange={(e) => onChange({ ...row, value: e.target.value })}
      />
    )
  const isDate = field?.type === 'date'
  const isNumber = field && ['integer', 'decimal', 'computed'].includes(field.type)
  return (
    <Input
      label="Valeur"
      fieldClassName="condition-value"
      type={isDate ? 'date' : isNumber ? 'number' : 'text'}
      value={'value' in row ? String(row.value ?? '') : ''}
      onChange={(e) =>
        onChange({
          ...row,
          value: isNumber && e.target.value !== '' ? Number(e.target.value) : e.target.value,
        } as Simple)
      }
    />
  )
}
